/**
 * Server Connection Context
 *
 * Global context for monitoring SATCHEL API server connectivity.
 * Provides real-time connection status across the entire application.
 *
 * Features:
 * - Continuous health polling (configurable interval)
 * - Electron IPC integration for embedded server status
 * - Blocking state when server is disconnected
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  SatchelApiClient,
  msSinceServerReachable,
  type ServerHealthInfo,
  type ServerHealthResult,
} from "@satchel/core";
import { isElectron } from "../utils/runtime";

/**
 * Connection status states
 */
export type ConnectionStatus = "checking" | "connected" | "disconnected";

/**
 * Embedded server status (Electron only)
 */
export interface EmbeddedServerStatus {
  running: boolean;
  pid: number | null;
  error: string | null;
  binaryMissing?: boolean;
}

/**
 * Server connection state
 */
export interface ServerConnectionState {
  /** Current connection status */
  status: ConnectionStatus;
  /** Last error message */
  lastError: string | null;
  /** Timestamp of last successful connection */
  lastConnected: Date | null;
  /** Timestamp of last health check */
  lastChecked: Date | null;
  /** Server info from last successful health check */
  serverInfo: ServerHealthInfo | null;
  /** Response latency in milliseconds */
  latency: number | null;
  /** Embedded server status (Electron only) */
  embeddedServer: EmbeddedServerStatus | null;
  /** Number of consecutive failures */
  failureCount: number;
  /** API base URL being checked */
  apiUrl: string;
}

/**
 * Server connection context value
 */
export interface ServerConnectionContextValue extends ServerConnectionState {
  /** Manually trigger a health check */
  checkNow: () => Promise<void>;
  /** Whether a check is in progress */
  isChecking: boolean;
}

/**
 * Default polling interval (5 seconds)
 */
const DEFAULT_POLL_INTERVAL = 5000;

/**
 * Number of consecutive failures before considering disconnected.
 * - Initial check: 1 failure (fast feedback on startup)
 * - Ongoing monitoring: 2 failures (prevents flapping on transient issues)
 */
const INITIAL_FAILURE_THRESHOLD = 1;
const ONGOING_FAILURE_THRESHOLD = 2;

/**
 * A health poll can time out while the server is busy with a long request. If
 * the server answered something else within this window it is busy, not gone,
 * and tearing the UI down would abandon the operation the user is waiting on.
 * Three poll intervals, so a genuine outage still trips the threshold promptly.
 */
const RECENTLY_REACHABLE_WINDOW = DEFAULT_POLL_INTERVAL * 3;

const ServerConnectionContext = createContext<ServerConnectionContextValue | null>(null);

export interface ServerConnectionProviderProps {
  children: React.ReactNode;
  /** Polling interval in milliseconds (default: 5000) */
  pollInterval?: number;
}

/**
 * Server Connection Provider
 *
 * Wraps the application and provides global server connection monitoring.
 */
export const ServerConnectionProvider: React.FC<ServerConnectionProviderProps> = ({
  children,
  pollInterval = DEFAULT_POLL_INTERVAL,
}) => {
  const apiClient = useMemo(() => new SatchelApiClient(), []);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCheckingRef = useRef(false);
  // Track if we've ever successfully connected (for failure threshold logic)
  const hasConnectedRef = useRef(false);

  const [state, setState] = useState<ServerConnectionState>(() => ({
    status: "checking",
    lastError: null,
    lastConnected: null,
    lastChecked: null,
    serverInfo: null,
    latency: null,
    embeddedServer: null,
    failureCount: 0,
    apiUrl: apiClient.getBaseUrl(),
  }));

  const [isChecking, setIsChecking] = useState(false);

  /**
   * Perform a health check and update state
   */
  const performHealthCheck = useCallback(async () => {
    // Prevent concurrent checks
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    setIsChecking(true);

    try {
      const result: ServerHealthResult = await apiClient.checkServerHealth();

      setState((prev) => {
        if (result.available) {
          // Mark that we've connected at least once
          hasConnectedRef.current = true;
          return {
            ...prev,
            status: "connected",
            lastError: null,
            lastConnected: new Date(),
            lastChecked: result.checkedAt,
            serverInfo: result.serverInfo || null,
            latency: result.latency || null,
            failureCount: 0,
          };
        }

        // Increment failure count
        const newFailureCount = prev.failureCount + 1;
        // Use lower threshold on initial load (before first successful connection)
        // for faster feedback; higher threshold after connection for stability
        const threshold = hasConnectedRef.current
          ? ONGOING_FAILURE_THRESHOLD
          : INITIAL_FAILURE_THRESHOLD;
        const shouldDisconnect =
          newFailureCount >= threshold && msSinceServerReachable() > RECENTLY_REACHABLE_WINDOW;

        return {
          ...prev,
          status: shouldDisconnect ? "disconnected" : prev.status,
          lastError: result.error || "Unknown error",
          lastChecked: result.checkedAt,
          latency: result.latency || null,
          failureCount: newFailureCount,
        };
      });
    } catch (error) {
      setState((prev) => {
        const newFailureCount = prev.failureCount + 1;
        const threshold = hasConnectedRef.current
          ? ONGOING_FAILURE_THRESHOLD
          : INITIAL_FAILURE_THRESHOLD;
        const shouldDisconnect =
          newFailureCount >= threshold && msSinceServerReachable() > RECENTLY_REACHABLE_WINDOW;
        return {
          ...prev,
          status: shouldDisconnect ? "disconnected" : prev.status,
          lastError: error instanceof Error ? error.message : "Health check failed",
          lastChecked: new Date(),
          failureCount: newFailureCount,
        };
      });
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, [apiClient]);

  /**
   * Public method to manually trigger a check
   */
  const checkNow = useCallback(async () => {
    // Reset failure count on manual retry to give immediate feedback
    setState((prev) => ({ ...prev, status: "checking", failureCount: 0 }));
    await performHealthCheck();
  }, [performHealthCheck]);

  /**
   * Set up Electron IPC listener for embedded server status
   */
  useEffect(() => {
    if (!isElectron || !window.electronAPI?.onServerStatusChanged) {
      return;
    }

    const unsubscribe = window.electronAPI.onServerStatusChanged((status) => {
      setState((prev) => ({
        ...prev,
        embeddedServer: {
          running: status.running,
          pid: status.pid,
          error: status.error,
          binaryMissing: status.error?.includes("not found") || false,
        },
      }));

      // If server just started, trigger a health check
      if (status.running && !status.error) {
        performHealthCheck();
      }
    });

    // Get initial status
    window.electronAPI.getServerStatus?.().then((status) => {
      if (status) {
        setState((prev) => ({
          ...prev,
          embeddedServer: {
            running: status.running,
            pid: status.pid,
            error: status.error,
            binaryMissing: status.error?.includes("not found") || false,
          },
        }));
      }
    });

    return unsubscribe;
  }, [performHealthCheck]);

  /**
   * Set up polling interval
   */
  useEffect(() => {
    // Initial check
    performHealthCheck();

    // Start polling
    pollIntervalRef.current = setInterval(performHealthCheck, pollInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [performHealthCheck, pollInterval]);

  const contextValue: ServerConnectionContextValue = useMemo(
    () => ({
      ...state,
      checkNow,
      isChecking,
    }),
    [state, checkNow, isChecking],
  );

  return (
    <ServerConnectionContext.Provider value={contextValue}>
      {children}
    </ServerConnectionContext.Provider>
  );
};

/**
 * Hook to access server connection state
 *
 * @throws Error if used outside of ServerConnectionProvider
 */
export const useServerConnection = (): ServerConnectionContextValue => {
  const context = useContext(ServerConnectionContext);
  if (!context) {
    throw new Error("useServerConnection must be used within a ServerConnectionProvider");
  }
  return context;
};

export default ServerConnectionContext;
